import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TargetUserPicker } from "@/components/admin/concierge/TargetUserPicker";
import { createConciergeSite } from "@/lib/admin/concierge-api";
import { toast } from "@/hooks/use-toast";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
};

export function CreateConciergeDialog({ open, onOpenChange, orgId }: Props) {
  const [userId, setUserId] = useState<string | null>(null);
  const [title, setTitle] = useState("Concierge");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const m = useMutation({
    mutationFn: () =>
      createConciergeSite({ org_id: orgId, user_id: userId as string, title: title.trim() || "Concierge" }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["concierge", "admin", "sites", orgId] });
      onOpenChange(false);
      setUserId(null);
      setTitle("Concierge");
      navigate(`/app/admin/concierge/${row.id}`);
    },
    onError: (e: any) =>
      toast({ title: "Could not create site", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Concierge site</DialogTitle>
          <DialogDescription>
            Pick a user in your org. You can edit everything else after creating.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Target user</Label>
            <TargetUserPicker orgId={orgId} value={userId} onChange={setUserId} excludeExisting />
          </div>
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!userId || m.isPending} onClick={() => m.mutate()}>
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateConciergeDialog;
